/**
 * Memoised orchestrator callbacks for the survey-kit-driven funnel.
 *
 * Splits the imperative wiring out of `<Funnel>` so the orchestrator stays
 * under the 100-line max-fn-length lint cap. Each callback delegates to
 * `useFunnelState` (engine) and `survey-kit/branching` (rules-driven next-
 * step decisions).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { chooseNextStepFromDecision, resolveBranching } from "@nextday/survey-kit";
import type { AnswerInput, BranchingContext, BranchingDecision } from "@nextday/survey-kit";
import { countedStepPosition } from "@nextday/funnel-engine";
import type { ChooseNextStep, Step } from "@nextday/funnel-engine";
import type { StepTransitionDirection } from "@nextday/funnel-ui";

import type { FunnelAnalyticsApi } from "@src/funnel/hooks/useFunnelAnalytics";
import type { FunnelDraftAnswers } from "@src/funnel/useFunnelDraft";
import type { FunnelConfig } from "@nextday/funnel-engine";

/** Cap step duration at 5 minutes to filter background-tab / locked-device inflation.
 * Must stay in sync with `MAX_STEP_DURATION_MS` from `@nextday/wae`. */
const MAX_STEP_DURATION_MS = 300_000;

export type UseFunnelHandlersArgs = {
  currentStep: Step | null;
  answers: FunnelDraftAnswers;
  setAnswers: (
    next: FunnelDraftAnswers | ((prev: FunnelDraftAnswers) => FunnelDraftAnswers),
  ) => void;
  advance: (options?: { chooseNextStep?: ChooseNextStep }) => void;
  goBack: () => void;
  /** Optional session context forwarded to `resolveBranching` for brand/character-aware rules. */
  context?: BranchingContext;
  /** Optional analytics emitter bag — when supplied the handlers fire the §8.1 imperative events. */
  analytics?: FunnelAnalyticsApi;
  /** Funnel config — used to resolve step_index for analytics events. */
  config?: FunnelConfig;
};

export type UseFunnelHandlersResult = {
  direction: StepTransitionDirection;
  isResolvingBranch: boolean;
  onAnswer: (answerKey: string, input: AnswerInput) => void;
  onAdvance: () => void;
  onBack: () => void;
};

export const useFunnelHandlers = ({
  currentStep,
  answers,
  setAnswers,
  advance,
  goBack,
  context,
  analytics,
  config,
}: UseFunnelHandlersArgs): UseFunnelHandlersResult => {
  const [direction, setDirection] = useState<StepTransitionDirection>("forward");
  const [isResolvingBranch, setIsResolvingBranch] = useState(false);

  const latestAnswersRef = useRef<FunnelDraftAnswers>(answers);
  useEffect(() => {
    latestAnswersRef.current = answers;
  }, [answers]);

  // Track when each step is viewed so we can compute duration_ms on completion.
  // Uses performance.now() for sub-ms precision. The generation counter
  // forces the effect to re-fire on self-cycles (A→A via goto rules)
  // where the step reference doesn't change.
  const viewedAtRef = useRef<Map<string, number>>(new Map());
  const [stepGen, setStepGen] = useState(0);
  useEffect(() => {
    if (!currentStep) return;
    viewedAtRef.current.set(currentStep.id, performance.now());
  }, [currentStep, stepGen]);

  // Mirror of the latest `context`. Kept in a ref for the same reason as
  // `latestAnswersRef`: the context object resolves once (loading → ready)
  // and we don't want adding it to `onAdvance`'s dep array to either
  // recreate the callback on every render or leave a stale application_id behind.
  const latestContextRef = useRef<BranchingContext | undefined>(context);
  useEffect(() => {
    latestContextRef.current = context;
  }, [context]);

  const configRef = useRef(config);
  configRef.current = config;

  const resolveStepIndex = (stepId: string): number | undefined => {
    const cfg = configRef.current;
    if (!cfg) return undefined;
    return countedStepPosition(cfg, stepId);
  };

  // Step types whose onAnswer calls represent incremental typing rather than
  // a discrete selection. For these, funnel_answer_selected is deferred to the
  // continue handler so we don't spam analytics with per-keystroke events.
  const TEXT_INPUT_STEP_TYPES: ReadonlySet<string> = useMemo(
    () => new Set(["name_capture", "text_short"]),
    [],
  );

  const onAnswer = useCallback(
    (answerKey: string, input: AnswerInput): void => {
      latestAnswersRef.current = { ...latestAnswersRef.current, [answerKey]: input.value };
      setAnswers((prev) => ({ ...prev, [answerKey]: input.value }));
      if (currentStep && !TEXT_INPUT_STEP_TYPES.has(currentStep.type)) {
        analytics?.funnel_answer_selected({
          stepId: currentStep.id,
          stepIndex: resolveStepIndex(currentStep.id),
          stepType: currentStep.type,
          answerKey,
          value: input.value,
        });
      }
      if (currentStep && input.action === "submit_email") {
        analytics?.emit("funnel_email_submitted", {
          step_id: currentStep.id,
          step_index: resolveStepIndex(currentStep.id),
        });
      }
    },
    [analytics, currentStep, setAnswers, TEXT_INPUT_STEP_TYPES],
  );

  const isResolvingBranchRef = useRef(false);

  const onAdvance = useCallback((): void => {
    if (!currentStep) return;
    if (isResolvingBranchRef.current) return;
    isResolvingBranchRef.current = true;
    const completedStepId = currentStep.id;
    setDirection("forward");
    setIsResolvingBranch(true);
    const emitCompletion = (): void => {
      setStepGen((g) => g + 1);
      const now = performance.now();
      const viewedAt = viewedAtRef.current.get(completedStepId);
      const rawMs = viewedAt !== undefined ? Math.round(now - viewedAt) : undefined;
      const durationMs = rawMs !== undefined ? Math.min(rawMs, MAX_STEP_DURATION_MS) : undefined;
      viewedAtRef.current.delete(completedStepId);
      if (currentStep && TEXT_INPUT_STEP_TYPES.has(currentStep.type)) {
        const answerKey = currentStep.data?.answerKey as string | undefined;
        if (answerKey) {
          analytics?.funnel_answer_selected({
            stepId: completedStepId,
            stepIndex: resolveStepIndex(completedStepId),
            stepType: currentStep.type,
            answerKey,
            value: latestAnswersRef.current[answerKey],
          });
        }
      }
      analytics?.funnel_step_completed({
        stepId: completedStepId,
        stepIndex: resolveStepIndex(completedStepId),
        answers: latestAnswersRef.current,
        duration_ms: durationMs,
        ...(currentStep?.type === "account_create" ? { immediate: true } : {}),
      });
      if (currentStep?.type === "account_create") {
        analytics?.emit("funnel_signup_completed", { step_id: completedStepId });
      }
    };

    void resolveBranching(
      currentStep,
      { answers: latestAnswersRef.current },
      latestContextRef.current,
    )
      .then((decision: BranchingDecision) => {
        // Reset the guard BEFORE calling advance() — React 18 may flush the
        // next step's render+commit+effects synchronously within this same
        // microtask (before .finally()), so any auto-advance effect on the
        // incoming step would see inFlight=true and be silently dropped.
        isResolvingBranchRef.current = false;
        setIsResolvingBranch(false);

        // Apply field overrides from `set_fields` rules into the answer bag
        // so downstream renderers (e.g. match_reveal) pick up the values.
        if (decision.fieldOverrides) {
          const overrides = decision.fieldOverrides;
          latestAnswersRef.current = { ...latestAnswersRef.current, ...overrides };
          setAnswers((prev) => ({ ...prev, ...overrides }));
        }

        // `stay` means the rules explicitly declined to progress (e.g.
        // validation failed). `chooseNextStepFromDecision` maps it to
        // `current.id`, so passing it to `advance()` would push a
        // duplicate step onto `history` and force the user to press
        // Back multiple times to escape. Short-circuit instead.
        if (decision.kind === "stay") return;
        advance({ chooseNextStep: chooseNextStepFromDecision(decision) });
        emitCompletion();
      })
      .catch(() => {
        // Rule evaluation should never strand the user. If a malformed
        // rule throws (bad operator, bad fact path, etc.), fall back to
        // the engine's linear next-step calculation so the funnel keeps
        // moving. The error itself is intentionally not surfaced — the
        // server-side lint catches authoring issues; the runtime's job
        // is to never block on them.
        isResolvingBranchRef.current = false;
        setIsResolvingBranch(false);
        advance();
        emitCompletion();
      });
  }, [advance, analytics, currentStep, setAnswers]);

  const onBack = useCallback((): void => {
    if (isResolvingBranchRef.current) return;
    if (currentStep) {
      analytics?.funnel_back_clicked({ fromStepId: currentStep.id });
      viewedAtRef.current.delete(currentStep.id);
    }
    setDirection("back");
    goBack();
  }, [analytics, currentStep, goBack]);

  return { direction, isResolvingBranch, onAnswer, onAdvance, onBack };
};

export default useFunnelHandlers;
